/**
 * Shared formatting for the Ticker Explorer sections.
 *
 * These sections each grew their own date helper, which drifted into three
 * different formats on one page — "Jul 31, 2026", "07/31/2026" and raw ISO.
 * One implementation keeps them in step.
 *
 * Every date in the Ticker Explorer is numeric. Month names are never
 * spelled out, not even abbreviated — contract expiries included, which is
 * where this last differed from the Wiseguy Alerts tool.
 *
 * Everything renders in **Eastern**. See utils/marketTime for why.
 */
import { TZ, etDate, parseMarketDate } from '../../utils/marketTime';

/**
 * US numeric date: MM/DD/YYYY, Eastern.
 *
 * Accepts ISO dates ("2026-07-31"), ISO datetimes, and the "YYYY-MM-DD HH:mm:ss"
 * shape some feeds return. Date-only strings are anchored to midday UTC by
 * `parseMarketDate` — parsing them bare makes them UTC midnight, which renders
 * as the PREVIOUS day anywhere west of Greenwich.
 */
export function fmtUsDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = parseMarketDate(s);
  return isNaN(d.getTime())
    ? s.slice(0, 10)
    : d.toLocaleDateString('en-US', {
        timeZone: TZ,
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
}

/**
 * Alert timestamp, matching the Wiseguy Alerts tool exactly: HH:mm for
 * today, M/D HH:mm once it rolls over. Kept byte-for-byte identical to the
 * widget so the same alert never reads two different ways in two places.
 */
export function fmtAlertTime(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const time = d.toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  // "Is it today" asked in market terms. `toDateString()` is local, so an
  // evening alert in New York showed a date prefix while still being today.
  if (etDate(d) === etDate(new Date())) return time;
  return `${d.toLocaleDateString('en-US', { timeZone: TZ, month: '2-digit', day: '2-digit' })} ${time}`;
}

/**
 * Alert time with the date ALWAYS shown, for panels spanning several sessions.
 *
 * ⚠️ `fmtAlertTime` omits the date for today, which is right in a list that is
 * mostly today and wrong the moment a table mixes sessions: a bare `09:51`
 * sitting between two `07/30` rows reads as a sorting fault rather than as
 * today's alert. Anything ranked by something other than time needs this one.
 */
export function fmtAlertDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';

  const date = d.toLocaleDateString('en-US', { timeZone: TZ, month: '2-digit', day: '2-digit' });
  const time = d.toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date} ${time}`;
}

/**
 * The underlying's price when the alert fired.
 *
 * ⚠️ `reference_price` arrives as a STRING and is sometimes absent — a bare
 * `parseFloat(...).toFixed(2)` renders the literal text "NaN" in the cell,
 * which reads as a broken number rather than a missing one.
 */
export function fmtRefPrice(v: string | number | null | undefined): string {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? '—' : n.toFixed(2);
}

/**
 * Dollar premium, compact: `$39`, `$328k`, `$1.4M`.
 *
 * ⚠️ **Null renders as a dash, not `$0`.** `/v1/option-trades` leaves `premium`
 * null on prints it could not value, and those still belong on a tape sorted by
 * time. `$0` would claim a print of no size, which is a different statement
 * from "not known" — and would sit misleadingly next to genuine small prints.
 */
export function fmtPremium(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';

  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)}k`;
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Compact magnitude with no currency mark: `2.67M`, `674.69K`, `1.5K`, `842`.
 *
 * Two significant decimals are kept at every scale, so the ranked column reads
 * as a ranking: `674.69K` above `674.12K` shows an order it holds, where the
 * rounder `675k` of `fmtPremium` would print both rows identically and make a
 * correct server ranking look arbitrary.
 *
 * ⚠️ **Not interchangeable with `fmtPremium`.** That one is for a time-ordered
 * tape where compactness wins and a dollar mark is wanted; this is for ranked
 * columns where adjacent rows are close and the header already says the unit.
 * Sharing one helper between them means one of the two reads wrong.
 *
 * ⚠️ Null renders as a dash, never `0` — the same rule the contract tables
 * apply everywhere else. A rendered `0` claims a contract traded nothing, which
 * is a different statement from the feed not valuing it.
 */
export function fmtCompactNum(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';

  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  // Trailing zeros are dropped so a round number reads as `2.6M`, not `2.60M`.
  const scaled = (n: number, suffix: string): string =>
    `${sign}${parseFloat(n.toFixed(2))}${suffix}`;

  if (abs >= 1e9) return scaled(abs / 1e9, 'B');
  if (abs >= 1e6) return scaled(abs / 1e6, 'M');
  if (abs >= 1e3) return scaled(abs / 1e3, 'K');
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
}
